import { z } from 'zod';
/**
 * The MCP endpoint (docs/adr/0088-mcp-server-and-estimates.md): JSON-RPC 2.0 over POST, JSON replies only. Written by
 * hand, so the shapes live here and the route registry can describe them.
 */
export const MCP_PROTOCOL_VERSION = '2025-06-18';
/** The most a request body may be, in bytes. */
export const MCP_MAX_BODY_BYTES = 256 * 1024;
export const JsonRpcId = z.union([z.string().max(200), z.number().int()]).nullable();
/** A request (has an id) or a notification (has none). Params are checked by each method. */
export const McpRequest = z.object({
    jsonrpc: z.literal('2.0'),
    id: JsonRpcId.optional(),
    method: z.string().min(1).max(100),
    params: z.record(z.string(), z.unknown()).optional(),
});
/** What the route answers: a JSON-RPC result or error. Left loose on purpose, each method has its own result. */
export const McpResponse = z.object({
    jsonrpc: z.literal('2.0'),
    id: JsonRpcId.optional(),
    result: z.unknown().optional(),
    error: z.object({ code: z.number().int(), message: z.string(), data: z.unknown().optional() }).optional(),
});
export const JSON_RPC_ERRORS = { parse: -32700, invalidRequest: -32600, methodNotFound: -32601, invalidParams: -32602, internal: -32603 };
/** The tools' argument schemas, one per tool. `tools/list` is built from these. */
const Id = z.uuid();
export const MCP_TOOLS = {
    list_clients: { description: 'List the workspace clients you can see (id, name).', input: z.object({}) },
    list_projects: { description: 'List the projects you can see (id, name, key, clientId).', input: z.object({ clientId: Id.optional() }) },
    list_people: { description: 'List the people in the workspace (userId, name, role) to assign tasks to.', input: z.object({}) },
    list_statuses: { description: 'List the task statuses (id, label, category, projectId: set for a stage only that project uses).', input: z.object({}) },
    list_sections: { description: "List a project's sections (id, name), in order, to place tasks with sectionId.", input: z.object({ projectId: Id }) },
    list_tasks: {
        description: 'List tasks, newest first (at most 100). Filter by project, client, assignee ("me" or a userId), open or closed, or text.',
        input: z.object({ projectId: Id.optional(), clientId: Id.optional(), assignee: z.union([z.literal('me'), Id]).optional(), open: z.boolean().optional(), q: z.string().max(100).optional() }),
    },
    get_task: { description: 'One task with its description, subtasks and the new deploy and estimate fields.', input: z.object({ taskId: Id }) },
    create_task: {
        description: 'Create a task. Needs a title; give projectId (or clientId) to place it, and optionally sectionId (a section of that project). Dates are YYYY-MM-DD, estimates are in minutes.',
        input: z.object({
            title: z.string().trim().min(1).max(200),
            description: z.string().max(20000).optional(),
            projectId: Id.nullable().optional(),
            clientId: Id.nullable().optional(),
            statusId: Id.optional(),
            priority: z.enum(['p1', 'p2', 'p3', 'p4']).optional(),
            assigneeUserId: Id.nullable().optional(),
            dueDate: z.iso.date().nullable().optional(),
            estimateMinutes: z.number().int().min(0).max(100000).nullable().optional(),
            labels: z.array(z.string().trim().min(1).max(32)).max(10).optional(),
            deployedTo: z.string().trim().min(1).max(40).nullable().optional(),
            deployedAt: z.iso.datetime().nullable().optional(),
            deployNotes: z.string().max(2000).optional(),
            estimateId: Id.nullable().optional(),
            estimateLineId: Id.nullable().optional(),
            /** A section of the task's project (docs/adr/0093). */
            sectionId: Id.nullable().optional(),
        }),
    },
    update_task: {
        description: 'Change a task: any of title, description, status, priority, assignee, dueDate, estimateMinutes, labels, section, deploy fields, estimate link.',
        input: z.object({
            taskId: Id,
            title: z.string().trim().min(1).max(200).optional(),
            description: z.string().max(20000).optional(),
            statusId: Id.optional(),
            priority: z.enum(['p1', 'p2', 'p3', 'p4']).optional(),
            assigneeUserId: Id.nullable().optional(),
            dueDate: z.iso.date().nullable().optional(),
            estimateMinutes: z.number().int().min(0).max(100000).nullable().optional(),
            labels: z.array(z.string().trim().min(1).max(32)).max(10).optional(),
            deployedTo: z.string().trim().min(1).max(40).nullable().optional(),
            deployedAt: z.iso.datetime().nullable().optional(),
            deployNotes: z.string().max(2000).optional(),
            estimateId: Id.nullable().optional(),
            estimateLineId: Id.nullable().optional(),
            /** A section of the task's project, or null for No section (docs/adr/0093). */
            sectionId: Id.nullable().optional(),
        }),
    },
    list_estimates: { description: 'List client estimates (needs the Invoicing feature).', input: z.object({ status: z.enum(['draft', 'sent', 'accepted', 'declined', 'expired']).optional(), clientId: Id.optional() }) },
    get_estimate: { description: 'One estimate with its lines.', input: z.object({ estimateId: Id }) },
    create_estimate: {
        description: 'Create a client estimate with lines. Amounts are in cents (1000 = 10.00). Totals are worked out by Chapsi.',
        input: z.object({
            clientId: Id,
            projectId: Id.nullable().optional(),
            title: z.string().trim().min(1).max(200),
            currency: z.string().regex(/^[A-Z]{3}$/).optional(),
            issueDate: z.iso.date().optional(),
            validUntil: z.iso.date().nullable().optional(),
            notes: z.string().max(5000).optional(),
            taxRateBp: z.number().int().min(0).max(10000).optional(),
            taxLabel: z.string().trim().max(20).optional(),
            status: z.enum(['draft', 'sent', 'accepted', 'declined', 'expired']).optional(),
            lines: z
                .array(z.object({ description: z.string().trim().min(1).max(500), quantity: z.number().min(0).max(100000), unitAmountCents: z.number().int().min(0).max(1_000_000_000), hours: z.number().min(0).max(100000).nullable().optional() }))
                .max(200)
                .default([]),
        }),
    },
    update_estimate: {
        description: 'Change an estimate: title, status, notes, tax, valid-until, or replace all its lines.',
        input: z.object({
            estimateId: Id,
            title: z.string().trim().min(1).max(200).optional(),
            projectId: Id.nullable().optional(),
            status: z.enum(['draft', 'sent', 'accepted', 'declined', 'expired']).optional(),
            validUntil: z.iso.date().nullable().optional(),
            notes: z.string().max(5000).optional(),
            taxRateBp: z.number().int().min(0).max(10000).optional(),
            taxLabel: z.string().trim().max(20).optional(),
            lines: z
                .array(z.object({ description: z.string().trim().min(1).max(500), quantity: z.number().min(0).max(100000), unitAmountCents: z.number().int().min(0).max(1_000_000_000), hours: z.number().min(0).max(100000).nullable().optional() }))
                .max(200)
                .optional(),
        }),
    },
};
//# sourceMappingURL=mcp.js.map